import { useState } from "react";

import { AssetSearch } from "~/v3-poc/features/asset-search/AssetSearch.connector";
import type { OrderLeg } from "../shared/optionLegs/optionLeg.types";
import { OrderTicketWidgetPresentation } from "../widgets/orderTicket/OrderTicketWidget.pres";
import { useOrderTicketSession } from "../widgets/orderTicket/useOrderTicketSession";
import { useOrderTicketDrawerState } from "./OrderTicketDrawer.context";
import { OrderTicketDrawerPresentation } from "./OrderTicketDrawer.pres";
import type { OrderTicketDraft } from "./OrderTicketDrawer.types";

const NO_LEGS: OrderLeg[] = [];

/**
 * Holds everything the user has entered, and sits *above* the drawer rather
 * than inside it: crossing the push breakpoint flips Radix between its modal
 * and non-modal `Dialog.Content` implementations, which remounts the whole
 * drawer subtree. Session state kept here survives that remount, so the ticket
 * body is rebuilt from the same values.
 */
const DrawerTicketSession = ({
  draft,
  open,
  modal,
  container,
  reducedMotion,
  onClose,
}: {
  draft: OrderTicketDraft;
  open: boolean;
  modal: boolean;
  container: HTMLElement | null;
  reducedMotion: boolean;
  onClose: () => void;
}) => {
  const [symbol, setSymbol] = useState(draft.symbol);
  const [legs, setLegs] = useState(() => [...(draft.legs ?? [])]);
  const session = useOrderTicketSession({
    // The session outlives a closed drawer (it wraps it), so blank the inputs
    // while closed to keep quote subscriptions off until the next opening.
    symbol: open ? symbol : undefined,
    legs: open ? legs : NO_LEGS,
    initialSide: draft.side,
    initialQuantity: draft.quantity,
    initialOrderType: draft.orderType,
    initialLimitPrice: draft.limitPrice,
    onSubmitSuccess: onClose,
  });

  return (
    <OrderTicketDrawerPresentation
      open={open}
      modal={modal}
      container={container}
      reducedMotion={reducedMotion}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      ticketBody={
        open ? (
          <OrderTicketWidgetPresentation
            {...session}
            symbol={symbol}
            legs={legs}
            symbolSearch={
              <AssetSearch
                showFilters={false}
                placeholder="Search symbol..."
                onSelect={setSymbol}
              />
            }
            onRemoveLeg={(index) =>
              setLegs((current) => current.filter((_, i) => i !== index))
            }
            onInteract={() => {}}
          />
        ) : null
      }
    />
  );
};

/**
 * One ticket session per drawer opening: the session is keyed on
 * `sessionRevision`, so opening the drawer starts from the new draft and
 * everything else about the drawer (responsive mode, reduced motion) only
 * changes props.
 */
export const OrderTicketDrawer = ({
  container,
}: {
  container: HTMLElement | null;
}) => {
  const state = useOrderTicketDrawerState();

  return (
    <DrawerTicketSession
      key={state.sessionRevision}
      draft={state.draft}
      open={state.open}
      modal={state.modal}
      container={container}
      reducedMotion={state.reducedMotion}
      onClose={state.closeOrderTicket}
    />
  );
};
